import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Info } from "lucide-react";
import { ArrowUpRight } from "lucide-react";

const PLANS = [
  {
    number: "01",
    name: "Starter Plan",
    nameColor: "#E154AA",
    subtitle: "Best for new startups launching their brand",
    items: [
      "Logo design",
      "Brand Identity (colors, typography)",
      "Website Design / Redesign",
      "Basic Social Media Handling",
    ],
    footer: "Everything you need to launch your brand from scratch.",
  },
  {
    number: "02",
    name: "Creator Plan",
    nameColor: "#129ADC",
    subtitle: "Best for creators and small businesses building online presence",
    items: [
      "Website Design / Redesign",
      "Social Media Handling (content + design + posting)",
    ],
    footer: "Build a strong digital presence and stay consistent.",
  },
  {
    number: "03",
    name: "Scale Plan",
    nameColor: "#111",
    subtitle: "Best for growing businesses focused on leads & growth",
    items: [
      "Social Media Handling (content + design + posting)",
      "SEO (basic + local)",
      "Performance Marketing (Meta / Google Ads)",
    ],
    footer: "Designed to grow your reach, traffic, and conversions.",
  },
  {
    number: "04",
    name: "Business Plan",
    nameColor: "#F45A39",
    subtitle: "Best for brands that want complete design + growth system",
    items: [
      "Logo & Full Branding",
      "Website Design",
      "Packaging Design",
      "Social Media Handling (content + design + posting)",
    ],
    footer: "Complete brand system built for impact and scale.",
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.09,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 24,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 110,
      damping: 18,
    },
  },
};

const PlanCard = ({ plan, isActive }) => (
  <motion.div variants={cardVariants} className={`plan-card ${isActive ? 'mobile-active' : ''}`} tabIndex={0}>
    <div>
      <div className="plan-number">{plan.number}</div>

      <h3 className="plan-name" style={{ color: plan.nameColor }}>
        {plan.name}
      </h3>

      <div className="plan-divider" />

      <p className="plan-subtitle">{plan.subtitle}</p>


      <ul className="plan-list">
        {plan.items.map((item, i) => (
          <li key={i} className="plan-item">
            <span className="plan-check">✓</span>
            {item}
          </li>
        ))}
      </ul>
    </div>

    <div>
      <p className="plan-footer">{plan.footer}</p>

      <div className="plan-cta">
        <button
          className="plan-cta-text"
          onClick={() => (window.location.href = "#contact")}
        >
          Get a Free Review <ArrowUpRight className="w-4 h-4" />
        </button>

        <button
          className="plan-cta-solid"
          onClick={() => (window.location.href = `whatsapp://send?phone=+919491105026&text=Hello%20Sintx!%20I%20am%20interested%20in%20the%20${plan.name}.`)}
        >
          Request a Quote
        </button>
      </div>
    </div>
  </motion.div>
);

const GrowthPlanSection = () => {
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const carouselRef = useRef(null);

  const handleScroll = () => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    
    const scrollCenter = container.scrollLeft + container.clientWidth / 2;
    
    let closestIndex = 0;
    let minDistance = Infinity;
    
    Array.from(container.children).forEach((child, index) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const distance = Math.abs(childCenter - scrollCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });
    
    setActiveCardIndex(closestIndex);
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

        .gp-section {
          background: #F8F8F8;
          padding: 72px 40px 80px;
          font-family: 'Poppins', sans-serif;
        }

        /* Header */

        .gp-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .gp-label {
          font-size: 20px;
          font-weight: 600;
          line-height: 28px;
          text-transform: uppercase;
          background: linear-gradient(
            135deg,
            #129ADC,
            #B55DD0,
            #E154AA,
            #F45A39
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
          display: inline-block;
        }

        .gp-heading {
          font-size: 36px;
          font-weight: 700;
          color: #111827;
          line-height: 52px;
          margin: 0;
        }

        /* Grid */

        .gp-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          max-width: 1160px;
          margin: 0 auto 32px;
          align-items: stretch;
          min-height: 380px;
          position: relative;
        }

        @media (max-width: 960px) {
          .gp-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
          }
          .plan-card {
            width: auto;
          }
        }

        @media (max-width: 768px) {
          .gp-grid {
            display: flex;
            flex-wrap: nowrap;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            margin: 0 -20px 32px -20px;
            padding: 0 20px 20px 20px;
            scrollbar-width: none;
            -ms-overflow-style: none;
            gap: 16px;
          }

          .gp-grid::-webkit-scrollbar {
            display: none;
          }

          .plan-card {
            flex: 0 0 85%;
            width: 85%;
            scroll-snap-align: center;
          }

          .plan-card.mobile-active {
            border: 2px solid transparent;
            background:
              linear-gradient(#fff, #fff) padding-box,
              linear-gradient(
                160deg,
                #129ADC 0%,
                #B55DD0 40%,
                #E154AA 70%,
                #F45A39 100%
              ) border-box;
            transform: translateY(-6px);
            box-shadow: 0 12px 40px rgba(181, 93, 208, 0.18);
          }

          .plan-card.mobile-active .plan-number {
            background: linear-gradient(
              135deg,
              #129ADC,
              #B55DD0,
              #E154AA,
              #F45A39
            );
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .plan-card.mobile-active .plan-cta {
            max-height: 140px;
            opacity: 1;
            margin-top: 18px;
          }

          .gp-section {
            padding: 50px 20px;
          }

          .gp-heading {
            font-size: 28px;
            line-height: 40px;
          }
        }

        /* Card */

        .plan-card {
          background: #fff;
          border: 1.5px solid #B6B6B6;
          border-radius: 18px;
          padding: 35px 28px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          position: relative;
          overflow: hidden;
          transition: all 0.35s ease;
          height: 100%;
          min-height: 420px;
          width: calc((100% / 4) - 24px);
        }

        .plan-card:hover {
          border: 2px solid transparent;
          background:
            linear-gradient(#fff, #fff) padding-box,
            linear-gradient(
              160deg,
              #129ADC 0%,
              #B55DD0 40%,
              #E154AA 70%,
              #F45A39 100%
            ) border-box;

          transform: translateY(-6px);

          box-shadow: 0 12px 40px rgba(181, 93, 208, 0.18);
        }

        /* Number */

        .plan-number {
          font-size: 2.6rem;
          font-weight: 700;
          color: #A8A8A8;
          line-height: 1;
          margin-bottom: 14px;
          letter-spacing: -0.02em;
          transition: all 0.35s ease;
        }

        .plan-card:hover .plan-number {
          background: linear-gradient(
            135deg,
            #129ADC,
            #B55DD0,
            #E154AA,
            #F45A39
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Name */

        .plan-name {
          font-size: 0.9rem;
          font-weight: 700;
          margin: 0 0 8px;
          text-underline-offset: 3px;

          background: linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
          width: fit-content;
        }

        /* Subtitle */

        .plan-subtitle {
          font-size: 0.75rem;
          color: #6B7280;
          line-height: 1.65;
          margin: 0 0 16px;
        }

        /* Divider */

        .plan-divider {
          height: 2px;
          width: 70%;
          background: #CACACA;
          margin-bottom: 16px;
        }

        /* List */

        .plan-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .plan-item {
          font-size: 0.75rem;
          color: #374151;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          line-height: 1.5;
        }

        .plan-check {
          color: #129ADC;
          font-weight: bold;
          flex-shrink: 0;
        }

        /* Footer */

        .plan-footer {
          font-size: 0.72rem;
          color: #9CA3AF;
          line-height: 1.6;
          margin-top: 18px;
          font-style: italic;
        }

        /* CTA */

        .plan-cta {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: all 0.35s ease;
          margin-top: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .plan-card:hover .plan-cta {
          max-height: 140px;
          opacity: 1;
          margin-top: 18px;
        }

        .plan-cta-text {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 600;
          color: #111;
          transition: opacity 0.25s ease;
        }

        .plan-cta-text:hover {
          opacity: 0.7;
        }

        .plan-cta-solid {
          width: 100%;
          border: none;
          border-radius: 999px;
          padding: 12px;
          color: white;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          background: linear-gradient(
            135deg,
            #129ADC,
            #B55DD0,
            #E154AA,
            #F45A39
          );
          transition: transform 0.25s ease;
        }

     

        /* Info Banner */

        .gp-info {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          max-width: 450px;
          margin: 0 auto;
          padding: 12px 24px;
          border-radius: 100px;
          border: 1.5px solid #E5E7EB;
          background: #fff;
          font-size: 0.75rem;
          color: #6B7280;
        }
      `}</style>

      <section className="gp-section">
        <div className="gp-header">
          <div className="gp-label">PICK YOUR GROWTH PLAN</div>
          <h2 className="gp-heading">From idea stage to full scale</h2>
        </div>

        <motion.div
          className="gp-grid"
          ref={carouselRef}
          onScroll={handleScroll}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {PLANS.map((plan, i) => (
            <PlanCard key={i} plan={plan} isActive={i === activeCardIndex} />
          ))}
        </motion.div>

        <motion.div
          className="gp-info"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Info size={14} color="#9CA3AF" />
          Plans can be customized based on your business needs.
        </motion.div>
      </section>
    </>
  );
};

export default GrowthPlanSection;